import { Footer, Navbar } from '@/components/layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/auth';
import {
    getListAppointmentsQueryKey,
    useListAppointments,
} from '@/hooks/use-api';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  Clock,
  Info,
  MapPin,
} from 'lucide-react';
import { Link } from 'wouter';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any },
  },
};

export default function Dashboard() {
  const { user } = useAuth();

  // In a real app we'd filter by logged in user ID. For now we fetch all or mock.
  const { data: appointments, isLoading } = useListAppointments(undefined, {
    query: { queryKey: getListAppointmentsQueryKey() },
  });

  // Get user's rank (default to Rookie if not set)
  const userRank = (user as any)?.rank || 'Rookie';
  const adminMessage = (user as any)?.adminMessage || null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge className="bg-green-500 hover:bg-green-600 text-white border-0">
            CONFIRMED
          </Badge>
        );
      case 'pending':
        return (
          <Badge
            variant="secondary"
            className="bg-yellow-500/20 text-yellow-700 hover:bg-yellow-500/30 border-yellow-500/30"
          >
            PENDING
          </Badge>
        );
      case 'completed':
        return (
          <Badge
            variant="outline"
            className="border-primary text-primary bg-primary/5"
          >
            COMPLETED
          </Badge>
        );
      case 'cancelled':
        return <Badge variant="destructive">CANCELLED</Badge>;
      default:
        return <Badge variant="secondary">{status.toUpperCase()}</Badge>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <Navbar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 sm:mb-10 gap-4"
        >
          <div>
            <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-wide mb-2">
              YOUR DASHBOARD
            </h1>
            <p className="font-sans text-slate-500 dark:text-slate-400 text-base sm:text-lg">
              Manage your training sessions and progress.
            </p>
          </div>
          <Link href="/book">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                className="font-rajdhani font-bold tracking-wider w-full sm:w-auto"
                size="lg"
              >
                BOOK NEW SESSION
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <motion.h2
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="font-rajdhani font-bold text-xl sm:text-2xl tracking-wide mb-4"
            >
              UPCOMING SESSIONS
            </motion.h2>

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Skeleton
                    key={i}
                    className="h-40 sm:h-48 w-full rounded-xl"
                  />
                ))}
              </div>
            ) : Array.isArray(appointments) && appointments.length > 0 ? (
              <div className="space-y-4">
                {appointments.slice(0, 5).map((apt, index) => (
                  <motion.div
                    key={apt.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <Card className="overflow-hidden border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex flex-col sm:flex-row">
                        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 sm:w-48 flex flex-col justify-center items-center shrink-0 border-b sm:border-b-0 sm:border-r border-slate-700">
                          <Calendar className="w-8 h-8 mb-2 text-primary" />
                          <div className="font-bebas text-2xl tracking-wider text-center">
                            {format(new Date(apt.scheduledAt), 'MMM dd, yyyy')}
                          </div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                              <h3 className="font-bebas text-xl sm:text-2xl tracking-wide">
                                {apt.courseTitle}
                              </h3>
                              {getStatusBadge(apt.status)}
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm font-sans text-slate-500 dark:text-slate-400 mb-4">
                              <span className="flex items-center gap-1.5">
                                <Clock className="w-4 h-4" />
                                {format(new Date(apt.scheduledAt), 'h:mm a')}
                              </span>
                              <span className="hidden sm:inline text-slate-300">
                                •
                              </span>
                              <span className="flex items-center gap-1.5">
                                <MapPin className="w-4 h-4" />
                                Chicago HQ
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-end mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <Button
                              variant="outline"
                              size="sm"
                              className="font-rajdhani font-bold tracking-wider text-xs"
                            >
                              VIEW DETAILS
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Card className="bg-white dark:bg-slate-900 border-dashed border-2 border-slate-200 dark:border-slate-800">
                  <CardContent className="flex flex-col items-center justify-center py-12 sm:py-16 text-center px-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                      <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
                    </div>
                    <h3 className="font-bebas text-2xl sm:text-3xl tracking-wide mb-2">
                      NO SESSIONS BOOKED
                    </h3>
                    <p className="font-sans text-slate-500 dark:text-slate-400 max-w-md mb-6 text-sm sm:text-base">
                      You don't have any upcoming training sessions scheduled.
                      Book a class to start your ascent.
                    </p>
                    <Link href="/book">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button className="font-rajdhani font-bold tracking-wider">
                          BROWSE CLASSES
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </motion.div>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          <div className="space-y-6">
            <motion.h2
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="font-rajdhani font-bold text-xl sm:text-2xl tracking-wide mb-4"
            >
              YOUR STATUS
            </motion.h2>

            {/* Admin Message */}
            {adminMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
                  <CardHeader>
                    <CardTitle className="font-rajdhani font-bold tracking-wider flex items-center gap-2 text-base sm:text-lg text-blue-900 dark:text-blue-100">
                      <Info className="w-5 h-5 text-blue-600" /> MESSAGE FROM
                      ADMIN
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-sans text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                      {adminMessage}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Card className="bg-gradient-to-br from-primary to-blue-600 text-primary-foreground border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="font-bebas text-2xl tracking-wider">
                    CERTIFICATION LEVEL
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl sm:text-5xl font-bebas tracking-wide mb-2">
                    {userRank.toUpperCase()}
                  </div>
                  <p className="text-primary-foreground/80 font-sans text-sm">
                    {userRank === 'Rookie'
                      ? 'Complete an Intermediate course to level up your status.'
                      : 'Keep training to advance to the next level!'}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Card className="border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="font-rajdhani font-bold tracking-wider flex items-center gap-2 text-base sm:text-lg">
                    <AlertCircle className="w-5 h-5 text-primary" /> REQUIRED
                    GEAR
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 font-sans text-sm text-slate-600 dark:text-slate-400">
                    {[
                      'ANSI approved hard hat',
                      'Steel-toe boots',
                      'Weather-appropriate clothing',
                      'Safety glasses',
                    ].map((item, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + i * 0.05, duration: 0.3 }}
                        className="flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                  <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-sans">
                    <strong>Note:</strong> All ropes, harnesses, and descent
                    control devices are provided by the school.
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
